'use client';
import { useState, useCallback, useEffect, useMemo } from 'react';
import { Modal, Icon, Button } from '@ui/components';
import { IFansOfConverstion, IFanConversationSet } from '@ui/components';
import { GroupMemberItem } from './group-info-modal/member-item';
import { GroupInfoLoadingState } from './group-info-modal/loading-state';
import { GroupInfoEmptyState } from './group-info-modal/empty-state';
import { GroupInfoErrorState } from './group-info-modal/error-state';
import { GroupInfoConversationHeader } from './group-info-modal/conversation-header';
import { GroupInfoSpinner } from './group-info-modal/spinner';

const ITEMS_PER_PAGE = 10;

interface IGroupInfoModal {
  isOpen: boolean;
  onClose: () => void;
  conversationId: string;
  conversationData?: IFanConversationSet | null;
  conversationName?: string;
  useGetConversationFansQuery: any;
}

export function GroupInfoModal({
  isOpen,
  onClose,
  conversationId,
  conversationData,
  conversationName,
  useGetConversationFansQuery,
}: IGroupInfoModal) {
  const [currentPage, setCurrentPage] = useState(1);
  const [allFans, setAllFans] = useState<IFansOfConverstion[]>([]);
  const displayedFans = useMemo(() => allFans, [allFans]);
  const handleLoadMore = useCallback(() => setCurrentPage((prev) => prev + 1), []);

  const {
    data: membersData,
    isLoading: isLoadingMembers,
    isFetching,
    error: membersError,
    refetch,
  } = useGetConversationFansQuery(
    {
      id: conversationId,
      page: currentPage,
      items_per_page: ITEMS_PER_PAGE,
    },
    {
      skip: !conversationId || !isOpen,
      refetchOnMountOrArgChange: true,
    },
  );

  const conversation = useMemo(() => conversationData?.conversation, [conversationData]);

  const memberCount = useMemo(() => {
    // If data hasn't loaded yet, return 0 to avoid flashing incorrect count
    if (membersData === undefined) {
      return 0;
    }
    const baseCount = membersData?.fans?.length || 0;
    return baseCount;
  }, [membersData]);
  const hasMorePages = useMemo(
    () => membersData && currentPage < membersData.total_pages,
    [membersData?.total_pages, currentPage],
  );

  const resetState = useCallback(() => {
    setCurrentPage(1);
    setAllFans([]);
  }, []);

  useEffect(() => {
    if (!membersData?.fans) {
      return;
    }

    if (membersData.fans.length === 0) {
      if (currentPage === 1) {
        setAllFans([]);
      }
      return;
    }

    setAllFans((prev) => {
      if (currentPage === 1) {
        return membersData.fans;
      }

      const existingIds = new Set(prev.map((fan: IFansOfConverstion) => fan.id));
      const newFans = membersData.fans.filter(
        (fan: IFansOfConverstion) => !existingIds.has(fan.id),
      );
      return [...prev, ...newFans];
    });
  }, [membersData, currentPage, isOpen]);

  useEffect(() => {
    if (isOpen && conversationId) {
      if (allFans.length === 0) {
        setCurrentPage(1);
        setTimeout(() => {
          refetch();
        }, 50);
      }
    }
  }, [isOpen, conversationId, refetch, allFans.length]);

  useEffect(() => {
    setCurrentPage(1);
    setAllFans([]);
    if (isOpen) {
      setTimeout(() => {
        refetch();
      }, 50);
    }
  }, [conversationId]);

  if (!conversation) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} title="Group Info" className="max-w-2xl w-full">
        <div className="flex-1 flex justify-center items-center py-16">
          <div className="text-center px-6">
            <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
              <Icon type="info" className="text-red-500 w-8 h-8" />
            </div>
            <div className="text-base font-semibold text-gray-900 mb-2">
              Failed to load conversation
            </div>
            <div className="text-sm text-gray-500 mb-6">Please try again later</div>
            <Button onClick={onClose} type="primary">
              Go Back
            </Button>
          </div>
        </div>
      </Modal>
    );
  }

  const isInitialLoading = isLoadingMembers && currentPage === 1;
  const showEndOfList = !isFetching && !hasMorePages && allFans.length > 0;

  return (
    <Modal
      key={conversationId}
      isOpen={isOpen}
      onClose={onClose}
      title={conversationName || conversation.name || 'Group Info'}
      className="max-w-3xl w-full max-h-[90vh] overflow-hidden pointer-events-auto">
      <div className="flex flex-col h-full max-h-[75vh] min-h-[50vh]">
        <GroupInfoConversationHeader conversation={conversation} memberCount={memberCount} />

        <div className="flex-1 overflow-y-auto bg-gradient-to-b from-white to-gray-50">
          {isInitialLoading ? (
            <GroupInfoLoadingState />
          ) : membersError ? (
            <GroupInfoErrorState onRetry={resetState} />
          ) : displayedFans.length === 0 ? (
            <GroupInfoEmptyState hasSearch={false} />
          ) : (
            <>
              {displayedFans.map((item, index) => (
                <GroupMemberItem
                  key={item.id}
                  item={item}
                  showBorder={index !== displayedFans.length - 1}
                />
              ))}

              {isFetching && currentPage > 1 && (
                <div className="py-8 flex items-center justify-center bg-gradient-to-b from-white to-gray-50">
                  <div className="flex flex-col items-center gap-3">
                    <GroupInfoSpinner size="sm" />
                    <span className="text-sm text-gray-500 font-medium">
                      Loading more members...
                    </span>
                  </div>
                </div>
              )}

              {!isFetching && hasMorePages && (
                <div className="py-3 pb-8 md:py-4 md:pb-6 flex items-center justify-center">
                  <button
                    onClick={handleLoadMore}
                    className="text-xs text-gray-500 hover:text-gray-700 hover:underline transition-colors">
                    Load more
                  </button>
                </div>
              )}

              {showEndOfList && (
                <div className="py-8 pb-10 text-center bg-gradient-to-b from-white to-gray-50">
                  <div className="inline-flex items-center gap-3 px-4 py-2 rounded-full bg-gray-100 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    <div className="w-2 h-2 rounded-full bg-gray-400" />
                    <span>{`You've reached the end`}</span>
                    <div className="w-2 h-2 rounded-full bg-gray-400" />
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </Modal>
  );
}
